import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiX,
  FiMail,
  FiPhone,
  FiBriefcase,
  FiLinkedin,
  FiMapPin,
  FiFileText
} from "react-icons/fi";

const ViewManagerModal = ({ isOpen, manager, onClose }) => {
  const navigate = useNavigate();
  const modalRef = useRef(null);

  // Handle click outside modal and Escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !manager) return null;

  const handleEditManager = () => {
    onClose();
    navigate(`/company/dashboard/brands/brand-managers/edit/${manager._id}`);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div ref={modalRef} className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 font-bold text-xl">
                {manager.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{manager.name}</h2>
              <p className="text-sm text-gray-500">{manager.position}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Basic Information
              </h3>
              
              <div>
                <label className="text-sm font-medium text-gray-500">Full Name</label>
                <p className="text-base text-gray-900 mt-1">{manager.name}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <FiMail className="text-gray-400" />
                  Email
                </label>
                <p className="text-base text-gray-900 mt-1">{manager.email}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <FiPhone className="text-gray-400" />
                  Phone Number
                </label>
                <p className="text-base text-gray-900 mt-1">{manager.phone}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                  <FiBriefcase className="text-gray-400" />
                  Position
                </label>
                <p className="text-base text-gray-900 mt-1">{manager.position}</p>
              </div>

              {manager.department && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Department</label>
                  <p className="text-base text-gray-900 mt-1">{manager.department}</p>
                </div>
              )}

              {manager.reportingTo && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Reporting To</label>
                  <p className="text-base text-gray-900 mt-1">{manager.reportingTo}</p>
                </div>
              )}

              {manager.employeeId && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Employee ID</label>
                  <p className="text-base text-gray-900 mt-1">{manager.employeeId}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">Status</label>
                <div className="mt-1">
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    manager.status === "active"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}>
                    {manager.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>

            {/* Contact & Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2">
                Contact & Address
              </h3>

              {manager.linkedin && (
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <FiLinkedin className="text-gray-400" />
                    LinkedIn Profile
                  </label>
                  <a
                    href={manager.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-base text-blue-600 hover:text-blue-800 mt-1 underline"
                  >
                    {manager.linkedin}
                  </a>
                </div>
              )}

              {(manager.address?.street || 
                manager.address?.city || 
                manager.address?.state || 
                manager.address?.zipCode || 
                manager.address?.country) && (
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-2 mb-2">
                    <FiMapPin className="text-gray-400" />
                    Address
                  </label>
                  <div className="text-base text-gray-900 space-y-1 pl-6">
                    {manager.address?.street && (
                      <p>{manager.address.street}</p>
                    )}
                    <p>
                      {[
                        manager.address?.city,
                        manager.address?.state,
                        manager.address?.zipCode
                      ].filter(Boolean).join(", ")}
                    </p>
                    {manager.address?.country && (
                      <p>{manager.address.country}</p>
                    )}
                  </div>
                </div>
              )}

              {manager.notes && (
                <div>
                  <label className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <FiFileText className="text-gray-400" />
                    Notes
                  </label>
                  <p className="text-base text-gray-900 mt-1 whitespace-pre-wrap">
                    {manager.notes}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">Created At</label>
                <p className="text-base text-gray-900 mt-1">
                  {new Date(manager.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>

              {manager.updatedAt && manager.updatedAt !== manager.createdAt && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Last Updated</label>
                  <p className="text-base text-gray-900 mt-1">
                    {new Date(manager.updatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
          <button
            onClick={handleEditManager}
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
          >
            Edit Manager
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewManagerModal;

